import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import sharpenUpsellStyles from './styles/sharpen-upsell-styles';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { html, nothing } from 'lit';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { StoreState } from '../../store/store.interface';
import { fetchAllSharpenProducts } from './thunks/sharpen-search-service-data.thunk';
import {
  SharpenProductData,
  SharpenSearchServiceData,
  SharpenSearchServiceProductData,
} from './interfaces/sharpen-search-service-product-data.interface';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';
import '../subscription/add-to-cart/subscription-add-to-cart';
import { SubscriptionProductData } from '../subscription/add-to-cart/subscription-add-to-cart';
import { getCart as shopGetCart, isCartEmpty } from '../cart/shop-cart/store/shop-cart.selectors';
import { Product } from '../cart/shop-cart/interfaces/shop-cart.interface';
import './selected-sharpen-product-isbn';
import { BusinessUnit } from '../checkout/shop-checkout.interface';
import emit from '../../common/events';
import {
  getSharpenWidgetTitle,
  isCartFailed,
  isCartSuccess,
  isSharpenPlusBundle,
  isSharpenSearchServiceProductFailed,
  isSharpenSearchServiceProductSuccess,
  redirectToSharpenPDP,
} from './utils/sharpen-upsell.utils';
import {
  getCurrentSelectedSharpenUpsellData,
  getSharpenSearchServiceProductData,
} from './store/sharpen-product-data.selector';
import {
  SHARPEN_DESCRIPTION,
  SHARPEN_PROGRAM_ID,
  SHARPEN_TILE_TITLE,
  SharpenBoughtWithAnalyticsDataType,
  SharpenPageType,
} from './constants/sharpen-page-type.constants';
import { mapSharpenSearchServiceToSSubscriptionProductData } from './mapping/sharpen-search-service-to-subscription-product-data.mapping';
import { setSelectedSharpenUpsellData } from './store/sharpen-product-data.store';
import { isTunnelPurchaseFlow } from '../../common/utils/util';
import '../sharpen/sharpen-legal-disclaimer';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import {
  determineBusinessUnit,
  getProductTypeInfo,
  getSharpenOrderFlowAnalyticsData,
} from '../../common/utils/analytics-util';
import { OrderType } from '../../common/constants/order-types';
import { getApiErrorMessage } from '../../common/utils/error-message.util';

@customElement('mhecomm-sharpen-upsell-widget')
export class SharpenUpsell extends connect(store)(BaseComponent) {
  static styles = [sharpenUpsellStyles];

  @property()
  store?: string;

  @property()
  pageType?: string;

  @property()
  heProgramSourceId?: string;

  @property({ type: Array })
  sharpenIsbnList?: string[];

  @property()
  heProductIsbn10?: string;

  @property()
  heProductIsbn13?: string;

  @property()
  heProductTitle?: string;

  @state()
  sharpenSearchServiceProductData?: SharpenSearchServiceData;

  @state()
  sharpenProductData: SubscriptionProductData[] = [];

  @state()
  initializeCartFailed = false;

  @state()
  isSharpenDataLoaded = false;

  @state()
  isSharpenAvailable = false;

  @state()
  isSearchServiceCallSuccedded = false;

  @state()
  private getCartFailed = false;

  @state()
  private isSearchServiceCallFailed = false;

  @state()
  private selectedSharpenProduct?: SharpenProductData;

  private determinePageType = true;

  private initialCartFetchInProgress = true;

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(getShopCart({ store: this.store }));
  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await store.dispatch(getShopCart({ store: this.store }));
  }

  async stateChanged(state: StoreState) {
    super.stateChanged(state);

    if (isCartSuccess(state)) {
      this.initialCartFetchInProgress = false;
    }

    if (isSharpenSearchServiceProductFailed(state)) {
      this.isSearchServiceCallFailed = true;
    }

    this._fetchSharpenProductsBySearchServiceApiCall(state);
    this._mapSharpenProductData(state);
    this.selectedSharpenProduct = getCurrentSelectedSharpenUpsellData(state);
    this._hideUpsellWidgetIfCartIsEmpty(state);

    if (this.initialCartFetchInProgress && isCartFailed(state)) {
      this.initialCartFetchInProgress = false;
      this.getCartFailed = true;
    }
  }

  renderComp() {
    if (this.isSearchServiceCallFailed) {
      return html`${nothing}`;
    }
    return html`
      ${this.getCartFailed ? this.renderErrorToast() : nothing}
      ${this.isSharpenAvailable && this.isSearchServiceCallSuccedded ? this.renderSharpenUpsellWidget() : nothing}
    `;
  }

  private renderErrorToast() {
    return html`
      ${this.pageType === SharpenPageType.MHP_PDP
        ? nothing
        : html`
            <mhecomm-toast
              type="primary"
              header="Error Fetching Items in the Cart"
              .body=${this.getErrorMessage()}
              ?autohide="${false}"
              @toast-close="${() => (this.getCartFailed = false)}"
            ></mhecomm-toast>
          `}
    `;
  }

  private getErrorMessage() {
    return getApiErrorMessage({ bold: false });
  }

  private getAdditionalDataForPDP(calledOnUpsellLoad: boolean) {
    const boughtWith: SharpenBoughtWithAnalyticsDataType = {
      title: this.heProductTitle ?? '',
      isbn: this.heProductIsbn10 ?? '',
      isbn13: this.heProductIsbn13 ?? '',
      programSourceId: this.heProgramSourceId ?? '',
    };
    return calledOnUpsellLoad ? { shownWith: boughtWith } : { boughtWith };
  }

  private getAdditionalDataForCartPage(calledOnUpsellLoad: boolean) {
    const state = store.getState() as StoreState;
    const sharpenMappedProducts: SharpenBoughtWithAnalyticsDataType[] = [];
    (shopGetCart(state)?.products ?? []).forEach((product) => {
      if (
        product.productIdentifier.businessUnit === BusinessUnit.HE &&
        this.sharpenIsbnList?.includes(product.productIdentifier?.programSourceId!)
      ) {
        sharpenMappedProducts.push({
          title: product.productMetadata.title,
          isbn: product.productIdentifier.isbn10 || '',
          isbn13: product.productIdentifier.isbn13 || '',
          programSourceId: product.productIdentifier.programSourceId ?? '',
        });
      }
    });
    return calledOnUpsellLoad ? { shownWith: sharpenMappedProducts } : { boughtWith: sharpenMappedProducts };
  }

  private getAdditionalDataOnAddedToCart(calledOnUpsellLoad = false) {
    if (this.pageType === SharpenPageType.HE_PDP) {
      return this.getAdditionalDataForPDP(calledOnUpsellLoad);
    } else if (this.pageType === SharpenPageType.CART) {
      return this.getAdditionalDataForCartPage(calledOnUpsellLoad);
    } else if (this.pageType === SharpenPageType.TUNNEL_CART) {
      return this.getAdditionalTunnelAnalytics();
    }
    return {};
  }

  private isCartPage() {
    return this.pageType === SharpenPageType.CART || this.pageType === SharpenPageType.TUNNEL_CART;
  }

  private isPdpPage() {
    return this.pageType === SharpenPageType.HE_PDP || this.pageType === SharpenPageType.MHP_PDP;
  }

  renderSharpenUpsellWidget() {
    const sharpenWidgetTitle = getSharpenWidgetTitle();
    return html`
      <div class="border-top border-bottom mt-4 mb-4" id="upsell-container">
        <h2
          id="container-title"
          class="mb-3 ${this.isPdpPage() ? 'text-center' : ''}"
          aria-live="polite"
          aria-atomic="true"
          aria-label="${sharpenWidgetTitle}"
        >
          ${sharpenWidgetTitle}
        </h2>
        <div
          class="mb-2 rounded-3 p-4 shadow
            ${this.isCartPage() ? 'w-100' : 'upsell-card-width'}
            ${this.isPdpPage() ? 'mx-auto' : ''}
          "
          id="sharpen-container-size"
        >
          <div class="d-flex flex-column flex-sm-row align-items-start gap-3">
            <!-- Left (fixed width) -->
            <div
              class="sharpen-product-img-border flex-shrink-0 bg-light d-flex justify-content-center justify-content-sm-start align-self-center"
              style="width: 140px; height:"
            >
              <img
                aria-label="Product Card Image"
                class="sharpen-product-img mw-100"
                src="${getImageSrcUrl(this.sharpenSearchServiceProductData?.id!)}"
                alt=""
              />
            </div>
            <!-- Right (fills rest) -->
            <div class="flex-grow-1">
              <a
                id="sharpen-title"
                aria-label="${SHARPEN_TILE_TITLE}"
                href="${redirectToSharpenPDP()}"
                class="d-block p-0 border-0 text-start"
                data-automation-id="sharpen-tile-title"
                @click="${(e: Event) => this._navigateToSharpenPDP(e)}"
              >
                ${SHARPEN_TILE_TITLE}
              </a>
              <!-- ISBN removed for now. not removing the code as i feel thats temporary
                <div id="isbn-block-info">
                  <mhecomm-selected-sharpen-product-isbn></mhecomm-selected-sharpen-product-isbn>
                </div>
              -->
              <p
                class="d-none d-md-block m-0 mt-2 pb-2"
                aria-live="polite"
                aria-atomic="true"
                aria-label="${SHARPEN_DESCRIPTION}"
              >
                ${SHARPEN_DESCRIPTION}
              </p>
              <div class="d-block d-md-none pb-2">
                <time class="mb-1 fs-small">
                  <span>
                    © ${this.selectedSharpenProduct?.copyrightYear}
                  </span>
                </time>
              </div>
            </div>
          </div>
          <div class="item row">
            <mhecomm-sharpen-legal-disclaimer ?isSharpenPlus=${true}></mhecomm-sharpen-legal-disclaimer>
            <div class="col-12">
              ${this.isSharpenDataLoaded
                ? html`<mhecomm-subscription-add-to-cart
                    .productData=${this.sharpenProductData!}
                    .programId=${this.sharpenSearchServiceProductData?.id!}
                    ?isCalledFromSharpenUpselWidget=${true}
                    .addToCartAdditionalAnalytics=${this.getAdditionalDataOnAddedToCart()}
                    .businessUnit=${BusinessUnit.HE}
                    ?capturePDPLocalStorage=${true}
                  ></mhecomm-subscription-add-to-cart>`
                : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _navigateToSharpenPDP(event: Event) {
    event.preventDefault();

    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'sharpen-title-click-redirect',
        label: SHARPEN_TILE_TITLE,
        comp: 'sharpen-upsel-widget',
        page: this.pageType,
      },
    });
    window.location.href = (event.target as HTMLAnchorElement).href;
  }

  async _fetchSharpenProductsBySearchServiceApiCall(state: StoreState) {
    if (this.determinePageType) {
      if (this.pageType === SharpenPageType.HE_PDP && this.heProgramSourceId) {
        if (this.sharpenIsbnList?.includes(this.heProgramSourceId)) {
          this._dispatchProductDataForSharpenProducts();
        }
      }
      if (this.pageType === SharpenPageType.CART) {
        if (isCartSuccess(state)) {
          const showSharpenUpsellWidget = this._updateSharpenAvailability(shopGetCart(state)?.products!);
          if (showSharpenUpsellWidget) {
            this._dispatchProductDataForSharpenProducts();
          }
        }
      }
      if (this.pageType === SharpenPageType.TUNNEL_CART) {
        if (isCartSuccess(state) && isSharpenPlusBundle(state, this.sharpenIsbnList)) {
          this._dispatchProductDataForSharpenProducts();
        }
      }
    }
  }

  _dispatchProductDataForSharpenProducts() {
    store.dispatch(fetchAllSharpenProducts());
    this.isSharpenAvailable = true;
    this.determinePageType = false;
  }

  _mapSharpenProductData(state: StoreState) {
    if (isSharpenSearchServiceProductSuccess(state)) {
      this.isSearchServiceCallSuccedded = true;
      this.sharpenSearchServiceProductData = getSharpenSearchServiceProductData(state);

      if (this.sharpenSearchServiceProductData) {
        if (
          this.sharpenSearchServiceProductData.scdfs &&
          this.sharpenSearchServiceProductData.scdfs.length &&
          !this.sharpenProductData.length
        ) {
          this.sharpenProductData = this.sharpenSearchServiceProductData.scdfs
            .map((data: SharpenSearchServiceProductData) => mapSharpenSearchServiceToSSubscriptionProductData(data))
            .sort((a: any, b: any) => a.duration - b.duration);
          store.dispatch(setSelectedSharpenUpsellData(this.sharpenProductData[0]));
          !this.isSharpenDataLoaded && this.emitUpsellOnLoadEvent(); // ensure the onload event is not triggerred in every side-efefct
          this.isSharpenDataLoaded = true;
        }
      }
    }
  }

  _updateSharpenAvailability(products: Product[]): boolean {
    // First pass: Check if any product matches SHARPEN_PROGRAM_ID
    for (const product of products) {
      if (product.productIdentifier?.programSourceId === SHARPEN_PROGRAM_ID) {
        return false;
      }
    }

    // Second pass: Check for any product which is mapped to sharpen by programSourceId
    for (const product of products) {
      if (this.sharpenIsbnList?.includes(product.productIdentifier?.programSourceId!)) {
        return true;
      }
    }
    return false;
  }

  _hideUpsellWidgetIfCartIsEmpty(state: StoreState) {
    if (this.pageType === SharpenPageType.CART && isCartEmpty(state)) {
      this.isSharpenAvailable = false;
      this.determinePageType = true;
    }
  }

  emitUpsellOnLoadEvent() {
    emit({
      element: this,
      name: 'view-frequently-bought-with',
      detail: this.dataLayerDetail(),
      dataLayer: true,
    });
  }

  getAdditionalTunnelConnectAnalytics() {
    return {
      orderType: OrderType.HE_CONNECT,
      ...getSharpenOrderFlowAnalyticsData(),
    };
  }

  getAdditionalTunnelAnalytics() {
    if (!isTunnelPurchaseFlow()) {
      return {};
    }
    const state = store.getState() as StoreState;
    const hasConnectProductInCart = state.shopCartState.getShopCart.data?.products.some(
      (product) => product.productMetadata.application === 'connect'
    );
    return {
      ...(hasConnectProductInCart && this.getAdditionalTunnelConnectAnalytics()),
    };
  }

  dataLayerDetail() {
    const state = store.getState() as StoreState;
    const selectedSharpenProduct =
      this.selectedSharpenProduct || getCurrentSelectedSharpenUpsellData(state) || this.sharpenProductData[0];

    return {
      label: getSharpenWidgetTitle(),
      url: window.location.href,
      isbn: selectedSharpenProduct?.isbn,
      isbn13: selectedSharpenProduct?.isbn13,
      title: selectedSharpenProduct?.productTitle,
      seoTitle: selectedSharpenProduct?.seoTitle,
      description: selectedSharpenProduct?.description,
      user: getUserDataForUserAnalytics(state, true),
      businessUnit: determineBusinessUnit(BusinessUnit.HE),
      copyrightYear: selectedSharpenProduct?.copyrightYear,
      publishDate: selectedSharpenProduct?.publishDate,
      contributors: selectedSharpenProduct?.contributors,
      availability: selectedSharpenProduct?.availability,
      instructorComp: selectedSharpenProduct?.instructorComp,
      isMaster: selectedSharpenProduct?.isMaster,
      productOrdered: 'purchasable',
      pageType: this.pageType?.toLowerCase(),
      ...getProductTypeInfo({ programId: SHARPEN_PROGRAM_ID }),
      ...this.getAdditionalDataOnAddedToCart(true),
      ...this.getAdditionalTunnelAnalytics(),
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-sharpen-upsell-widget': SharpenUpsell;
  }
}
